import { ApiResponseSerializer } from '@v2/commons/interface/ApiResponseSerializer'
import {
  type IEscalation,
  IEscalationFilters,
  type IFormTemplate,
  type MutateEscalationData,
} from '@v2/domains/escalation/escalation.types'
import { TAGS_TASK } from '@v2/domains/task/api/task.api'
import { rootApi } from '@v2/store/api/rootApi'

export const TAGS_ESCLATION = {
  EscalationByPersonId: 'EscalationByPersonId',
  escalations: 'Escalation',
}

export const escalationApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getEscalations: builder.query<
      ApiResponseSerializer<IEscalation>,
      IEscalationFilters
    >({
      query: (params) => ({
        url: '/escalation',
        method: 'GET',
        params,
      }),
      providesTags: (result) =>
        result?.records.length
          ? [
              ...result.records.map(({ id }) => ({
                type: TAGS_ESCLATION.escalations,
                id,
              })),
              TAGS_ESCLATION.escalations,
            ]
          : [TAGS_ESCLATION.escalations],
    }),
    createEscalation: builder.mutation<IEscalation, MutateEscalationData>({
      query: (queryBody) => ({
        url: '/escalation',
        method: 'POST',
        data: queryBody,
      }),
      invalidatesTags: [TAGS_TASK.TaskByPersonId, TAGS_ESCLATION.escalations],
    }),
    updateEscalation: builder.mutation<
      IEscalation,
      { data: MutateEscalationData; id: string }
    >({
      query: ({ data, id }) => ({
        url: `/escalation/${id}`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: (result, __, args) =>
        result
          ? [
              { type: TAGS_ESCLATION.EscalationByPersonId, id: args.id },
              TAGS_TASK.TaskByPersonId,
            ]
          : [TAGS_TASK.TaskByPersonId, TAGS_ESCLATION.escalations],
    }),
    updateEscalationForm: builder.mutation<
      IEscalation['form'],
      {
        data: Pick<MutateEscalationData, 'form' | 'assignedToId'>
        id: string
      }
    >({
      query: ({ data, id }) => ({
        url: `/escalation/form/${id}`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: (result, __, args) =>
        result
          ? [
              { type: TAGS_ESCLATION.EscalationByPersonId, id: args.id },
              TAGS_TASK.TaskByPersonId,
            ]
          : [TAGS_TASK.TaskByPersonId, TAGS_ESCLATION.escalations],
    }),
    getEscalationById: builder.query<IEscalation, string>({
      query: (escalationId: string) => {
        return {
          url: `/escalation/${escalationId}`,
          method: 'GET',
        }
      },
      providesTags: (_, __, escalationId) => [
        { type: TAGS_ESCLATION.EscalationByPersonId, id: escalationId },
      ],
    }),
    getEscalationFormTemplate: builder.query<IFormTemplate, string>({
      query: (formTemplateId: string) => {
        return {
          url: `v1/forms/template/${formTemplateId}`,
          method: 'GET',
        }
      },
    }),
    deleteEscalation: builder.mutation<{ ok: true }, string>({
      query: (escalationId: string) => {
        return {
          url: `/escalation/${escalationId}`,
          method: 'DELETE',
        }
      },
      invalidatesTags: [TAGS_TASK.TaskByPersonId, TAGS_ESCLATION.escalations],
    }),
  }),
})

export const {
  useCreateEscalationMutation,
  useGetEscalationByIdQuery,
  useUpdateEscalationMutation,
  useGetEscalationFormTemplateQuery,
  useUpdateEscalationFormMutation,
  useGetEscalationsQuery,
  useDeleteEscalationMutation,
} = escalationApi
